import React, { useState, useEffect } from 'react';
import { Plus, Copy, Trash2, Edit, Calendar, FileText, Loader2 } from 'lucide-react';
import { apiClient } from '../../services/apiClient';
import { ConfirmationModal } from '../ConfirmationModal';

interface MenuDashboardProps {
  userId: string;
  onSelectMenu: (menuId: string) => void;
  onCreateMenu: () => void;
  onShowToast?: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

interface MenuSummary {
  id: string;
  title_en: string;
  title_ar?: string;
  created_at: string;
  updated_at: string;
}

export const MenuDashboard: React.FC<MenuDashboardProps> = ({ userId, onSelectMenu, onCreateMenu, onShowToast }) => {
  const [menus, setMenus] = useState<MenuSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  
  const [deleteModal, setDeleteModal] = useState<{
      isOpen: boolean;
      menuId: string | null;
  }>({ isOpen: false, menuId: null });

  const loadMenus = async () => {
    setIsLoading(true);
    try {
      const list = await apiClient.menuBuilder.listMenus(userId);
      setMenus(list);
    } catch (err) {
      console.error('Failed to load menus', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMenus();
  }, [userId]);

  const handleCopy = async (menuId: string) => {
    setActionLoading(menuId);
    try {
      await apiClient.menuBuilder.copyMenu(menuId);
      await loadMenus();
      onShowToast?.('success', 'Menu Copied', 'A duplicate menu was created.');
    } catch (err) {
      console.error('Failed to copy menu', err);
      onShowToast?.('error', 'Copy Failed', 'Could not copy this menu.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.menuId) return;
    setActionLoading(deleteModal.menuId);
    setDeleteModal({ isOpen: false, menuId: null });
    try {
      await apiClient.menuBuilder.deleteMenu(deleteModal.menuId);
      await loadMenus();
      onShowToast?.('success', 'Menu Deleted', 'The menu was removed.');
    } catch (err) {
      console.error('Failed to delete menu', err);
      onShowToast?.('error', 'Delete Failed', 'Could not delete this menu.');
    } finally {
      setActionLoading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <Loader2 className="animate-spin mr-2" /> Loading menus...
      </div>
    );
  }

  return (
    <div className="p-8 h-full overflow-y-auto bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">My Menus</h1>
            <p className="text-slate-500 mt-1">Manage your restaurant menus across different brands and branches.</p>
          </div>
          <button 
            onClick={onCreateMenu}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 shadow-sm transition-all active:scale-95"
          >
            <Plus size={20} /> Create New Menu
          </button>
        </div>

        {menus.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border-2 border-dashed border-slate-200">
            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
              <FileText size={32} />
            </div>
            <h3 className="text-xl font-bold text-slate-700 mb-2">No Menus Yet</h3>
            <p className="text-slate-500 mb-6">Create your first menu to get started.</p>
            <button 
              onClick={onCreateMenu}
              className="text-emerald-600 font-bold hover:underline"
            >
              Create a Menu Now
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {menus.map(menu => (
              <div key={menu.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow group">
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center text-emerald-600">
                      <FileText size={24} />
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => handleCopy(menu.id)}
                        disabled={!!actionLoading}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Duplicate"
                      >
                        {actionLoading === menu.id ? <Loader2 size={18} className="animate-spin"/> : <Copy size={18} />}
                      </button>
                      <button 
                        onClick={() => setDeleteModal({ isOpen: true, menuId: menu.id })}
                        disabled={!!actionLoading}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-bold text-slate-900 mb-1 truncate" title={menu.title_en}>{menu.title_en}</h3>
                  {menu.title_ar && <p className="text-sm text-slate-500 font-arabic mb-4 text-right" dir="rtl">{menu.title_ar}</p>}
                  
                  <div className="flex items-center gap-4 text-xs text-slate-400 mt-4 pt-4 border-t border-slate-100">
                    <span className="flex items-center gap-1">
                      <Calendar size={12} /> Updated {new Date(menu.updated_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                
                <div className="bg-slate-50 p-4 border-t border-slate-100 flex gap-3">
                  <button 
                    onClick={() => onSelectMenu(menu.id)}
                    className="flex-1 py-2 bg-white border border-slate-200 text-slate-700 font-bold rounded-lg hover:border-emerald-500 hover:text-emerald-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Edit size={16} /> Edit Menu
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmationModal 
        isOpen={deleteModal.isOpen}
        type="danger"
        title="Delete Menu?"
        message="Are you sure you want to delete this menu? This action cannot be undone."
        onConfirm={handleDelete}
        onCancel={() => setDeleteModal({ isOpen: false, menuId: null })}
        confirmText="Delete"
        isDestructive
      />
    </div>
  );
};
